import React, { useState, useEffect } from "react";
import VideoUploadStep from "../../components/Driver/VideoUploadStep";
import API from "../../config/config";
import { useNavigate } from "react-router-dom";

const carTypes = ["Hatchback", "Sedan", "SUV", "Luxury", "All"];

const UploadDrivingVideos = () => {
  const [step, setStep] = useState(0);
  const [selectedCarType, setSelectedCarType] = useState(null);
  const [popup, setPopup] = useState(null); // { type: 'success' | 'error', text: string }
  const navigate = useNavigate();

  const [videoURLs, setVideoURLs] = useState({
    transmission: "",
    front: "",
    reverse: "",
  });

  const token = localStorage.getItem("userPublicToken");

  const showPopup = (type, text) => {
    setPopup({ type, text });
  };

  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        const res = await fetch(API.ADMIN.USERDASHBOARD, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        const trainingVideos = data?.data?.trainingVideos;

        if (trainingVideos) {
          setSelectedCarType(trainingVideos.carType || null);
          setVideoURLs({
            transmission: trainingVideos.transmission_video || "",
            front: trainingVideos.front_drive_video || "",
            reverse: trainingVideos.reverse_parking_video || "",
          });
        }
      } catch (err) {
        console.error("Error fetching existing video data:", err);
      }
    };

    fetchVideoData();
  }, []);

  // Auto-hide popup
  useEffect(() => {
    if (popup) {
      const timer = setTimeout(() => setPopup(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [popup]);

  const uploadVideoToS3 = async (type, file) => {
    const formData = new FormData();
    formData.append("type", type);
    formData.append("video", file);
    formData.append("uploadTo", "s3");

    const res = await fetch(API.ADMIN.VIDEO_UPLOAD, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Video upload failed");
    return data.data.videoUrl;
  };

  const handleVideoUpload = async (currentStep, file) => {
    try {
      let uploadedUrl = "";

      if (file) {
        // Upload based on step
        const uploadTypeMap = {
          1: "transmission",
          2: "front_drive",
          3: "reverse_parking",
        };

        uploadedUrl = await uploadVideoToS3(uploadTypeMap[currentStep], file);

        if (currentStep === 1) {
          setVideoURLs((prev) => ({ ...prev, transmission: uploadedUrl }));
        } else if (currentStep === 2) {
          setVideoURLs((prev) => ({ ...prev, front: uploadedUrl }));
        } else if (currentStep === 3) {
          setVideoURLs((prev) => ({ ...prev, reverse: uploadedUrl }));
        }
      } else {
        // Use previously saved video if no new upload
        if (currentStep === 3 && !videoURLs.reverse) {
          throw new Error("Reverse parking video not available.");
        }
      }

      if (currentStep === 3) {
        const payload = {
          carType: selectedCarType,
          transmission_video: videoURLs.transmission,
          front_drive_video: videoURLs.front,
          reverse_parking_video: uploadedUrl || videoURLs.reverse,
        };

        await saveDrivingVideoData(payload);
        showPopup("success", "All videos uploaded and saved successfully!");
        navigate("/user-dashboard");
        setStep(0);
        setSelectedCarType(null);
        setVideoURLs({ transmission: "", front: "", reverse: "" });
      } else {
        setStep(currentStep + 1); // ✅ Move to next step
        showPopup("success", `Step ${currentStep} video handled successfully.`);
      }
    } catch (err) {
      console.error(err);
      showPopup("error", "Video upload failed: " + err.message);
    }
  };

  const saveDrivingVideoData = async (payload) => {
    const res = await fetch(API.ADMIN.SAVE_DRIVING_VIDEOS, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to save video data");
  };

  const renderStep = () => {
    const commonNotes =
      "Ensure you are driving the correct car based on your transmission selection.";

    if (step === 0) {
      return (
        <div className="max-w-3xl mx-auto p-6 bg-white rounded shadow">
          <button
            onClick={() => window.history.back()}
            className="mb-4 text-blue-600 hover:underline flex items-center"
          >
            ← Back
          </button>
          <h1 className="text-2xl font-bold mb-4">Upload Car Driving Videos</h1>

          <p className="mb-3">Instructions before uploading videos:</p>
          <ul className="list-disc pl-6 text-gray-700 mb-4">
            <li>Drive manual car only as per your driving choice.</li>
          </ul>

          <div className="mb-4">
            <h2 className="font-semibold mb-2">Your Driving Car Type</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {carTypes.map((type) => (
                <label
                  key={type}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="carType"
                    value={type}
                    checked={selectedCarType === type}
                    onChange={() => setSelectedCarType(type)}
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-yellow-100 p-4 rounded text-sm mb-4">
            <p>
              You have to drive a <strong>manual car</strong> if you selected
              transmission type as <strong>manual</strong>.
            </p>
            <p>
              You can drive both <strong>Manual and Automatic cars</strong> if
              you selected transmission type as{" "}
              <strong>Manual and Automatic</strong>.
            </p>
          </div>

          <button
            disabled={!selectedCarType}
            onClick={() => setStep(1)}
            className={`mt-4 px-4 py-2 rounded ${
              selectedCarType
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"
            }`}
          >
            Continue
          </button>
        </div>
      );
    }

    const stepProps = {
      onUpload: handleVideoUpload,
      notes: commonNotes,
    };

    if (step === 1) {
      return (
        <VideoUploadStep
          step={1}
          title="Step 1: Upload Car Transmission Type Video"
          exampleVideoUrl="/example-videos/gear.mp4"
          uploadedUrl={videoURLs.transmission}
          instructions={["Capture pedal area and gear rod clearly."]}
          onBack={() => setStep(0)}
          {...stepProps}
        />
      );
    }

    if (step === 2) {
      return (
        <VideoUploadStep
          step={2}
          title="Step 2: Upload 1 KM Front Driving with U-turn Video"
          exampleVideoUrl="/example-videos/front-driving.mp4"
          uploadedUrl={videoURLs.front}
          instructions={[
            "Show left side of the car outside.",
            "Capture driver sometimes and front road sometimes.",
            "Include U-turn driving views.",
          ]}
          onBack={() => setStep(1)}
          {...stepProps}
        />
      );
    }

    if (step === 3) {
      return (
        <VideoUploadStep
          step={3}
          title="Step 3: Upload Reverse & Front Parking Video"
          exampleVideoUrl="/example-videos/parking.mp4"
          uploadedUrl={videoURLs.reverse}
          instructions={[
            "Reverse park between two objects or vehicles.",
            "Front park between two objects or vehicles.",
          ]}
          onBack={() => setStep(2)}
          {...stepProps}
        />
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      {popup && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded shadow-lg text-sm z-50 ${
            popup.type === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {popup.text}
        </div>
      )}
      {renderStep()}
    </div>
  );
};

export default UploadDrivingVideos;
